"use client";

import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";

const caveat = Caveat({
  subsets: ["latin"],
  weight: ["700"],
  variable: "--font-caveat",
});

/* ─────────────────────────────────────────────────────────────
   1. HVAC SERVICES: OPTIMIZED FIELD SCHEDULING SECTION
   Exact reproduction of Odoo's Field Service Planning & Map UI
   ───────────────────────────────────────────────────────────── */
export function HvacOptimizedFieldSchedulingSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-center">

          {/* LEFT COLUMN: TITLE & COPYWRITING */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-5 text-left"
          >
            {/* Handwritten title with teal wavy underline under "scheduling" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <span className="block">Optimized field</span>
              <span className="relative inline-block">
                scheduling
                <svg
                  className="absolute -bottom-1 left-0 w-full h-4 overflow-visible"
                  viewBox="0 0 200 12"
                  fill="none"
                >
                  <path
                    d="M 4 6 Q 50 0, 100 6 T 196 6"
                    stroke="#2DD4BF"
                    strokeWidth="5"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </span>
            </h2>

            {/* Copywriting */}
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Plan, assign, and track</strong> technician tasks with a drag-and-drop calendar and map view, balancing workloads and ensuring timely service delivery.
            </p>

            {/* Bottom Blue Map Pin SVG Doodle Accent */}
            <div className="pt-2">
              <svg className="w-16 h-20 text-sky-400" viewBox="0 0 60 80" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {/* Pin head */}
                <path d="M 30 5 C 15 5, 5 18, 5 28 C 5 42, 30 70, 30 70 C 30 70, 55 42, 55 28 C 55 18, 45 5, 30 5 Z" fill="#38BDF8" fillOpacity="0.25" stroke="#0EA5E9" strokeWidth="2.5" />
                {/* Inner circle */}
                <circle cx="30" cy="28" r="8" fill="#0EA5E9" />
                <circle cx="27" cy="25" r="3" fill="#FFFFFF" />
                {/* Bottom squiggle */}
                <path d="M 20 74 Q 30 80, 40 74" stroke="#2DD4BF" strokeWidth="3" fill="none" />
              </svg>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: TWO ODOO WINDOW CARDS (Small & Precise, max-w-[580px]) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-5xl space-y-3">

              {/* TOP CARD: WEEKLY PLANNING VIEW (Field Service) */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 text-slate-800 text-[8px] p-2.5 font-sans">

                {/* Top Nav */}
                <div className="flex flex-wrap items-center justify-between border-b border-slate-100 pb-1.5 mb-1.5 text-[7.5px] text-slate-500 gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-rose-500 font-bold">🚀 Field Service</span>
                    <span>My Tasks</span>
                    <span>All Tasks</span>
                    <span className="font-semibold text-slate-800">Planning</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🔔</span><span>⚙️</span><span className="font-bold text-slate-700">BreezeMasters</span>
                  </div>
                </div>

                {/* Sub Nav: New + Planning by User */}
                <div className="flex items-center gap-2 border-b border-slate-100 pb-1.5 mb-1.5 text-[7.5px]">
                  <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                  <span className="font-semibold text-slate-700">Planning by User ⚙️</span>
                  <div className="ml-auto flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[7px] text-slate-400">
                    <span>👤 Assignees ×</span>
                    <span>🔍 Search...</span>
                  </div>
                  <div className="flex items-center gap-1 text-[7px] text-slate-400">
                    <span>← →</span>
                    <span className="bg-slate-100 px-1.5 py-0.5 rounded">Week ▾</span>
                    <span className="bg-slate-100 px-1.5 py-0.5 rounded">Today</span>
                    <span>🗓️</span>
                  </div>
                </div>

                {/* Week Planning Header Row */}
                <div className="grid grid-cols-6 gap-1 text-[7px] text-slate-600 border-b border-slate-100 pb-1 mb-1">
                  <div className="text-slate-400 font-medium">Planning</div>
                  <div className="text-center text-slate-500">Mon 8</div>
                  <div className="text-center text-slate-500">Tue 9</div>
                  <div className="text-center font-bold text-slate-900 bg-amber-50 rounded px-1">Wed 10</div>
                  <div className="text-center text-slate-500">Thu 11</div>
                  <div className="text-center text-amber-600 font-semibold">Fri 12</div>
                </div>

                {/* Planning Rows */}
                {[
                  { name: "Unassigned", color: "bg-slate-300", tasks: [null, null, null, null, null] },
                  { name: "Andrew Ma.", color: "bg-emerald-400", tasks: ["9:00 AM", null, null, null, null] },
                  { name: "Ethan Delg.", color: "bg-sky-400", tasks: [null, null, "9:00 AM", null, null] },
                  { name: "Tom White", color: "bg-purple-400", tasks: [null, null, null, null, "9:00 AM"] },
                ].map((row, i) => (
                  <div key={i} className="grid grid-cols-6 gap-1 text-[7px] border-b border-slate-50 py-1 items-center">
                    <div className="flex items-center gap-1 truncate">
                      <span className={`w-2 h-2 rounded-full ${row.color} flex-shrink-0`} />
                      <span className="text-slate-700 font-medium truncate">{row.name}</span>
                    </div>
                    {row.tasks.map((t, j) => (
                      <div key={j} className={`text-center ${j === 2 ? "bg-amber-50" : ""} rounded`}>
                        {t ? (
                          <span className="bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded text-[6.5px] font-semibold">{t}</span>
                        ) : (
                          <span className="text-slate-100">—</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* BOTTOM CARD: MAP VIEW (Field Service + Google Map UI) */}
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 text-slate-800 text-[8px] p-2.5 font-sans">

                {/* Top Nav */}
                <div className="flex flex-wrap items-center justify-between border-b border-slate-100 pb-1.5 mb-1.5 text-[7.5px] text-slate-500 gap-1">
                  <div className="flex items-center gap-2">
                    <span className="text-rose-500 font-bold">🚀 Field Service</span>
                    <span>My Tasks</span>
                    <span>All Tasks</span>
                    <span>Planning</span>
                    <span>Reporting</span>
                    <span>Configuration</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>🔔</span><span className="font-bold text-slate-700">BreezeMasters</span>
                  </div>
                </div>

                {/* Sub Nav */}
                <div className="flex items-center gap-1 border-b border-slate-100 pb-1.5 mb-1.5 text-[7.5px]">
                  <div className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-[7px] text-slate-500">
                    <span>☑ My Tasks ×</span>
                    <span>📅 Today ×</span>
                    <span>🔍</span>
                  </div>
                  <span className="text-slate-400 ml-1 text-[7px]">1-3 / 3  ❮ ❯</span>
                </div>

                {/* Map layout: left list + right map */}
                <div className="grid grid-cols-12 gap-2">

                  {/* Left Task List */}
                  <div className="col-span-4 space-y-1 text-[7.5px]">
                    <div className="font-bold text-slate-800 flex items-center gap-1">
                      <span>🗺 Map ⚙️</span>
                    </div>
                    <button className="w-full bg-[#1A73E8] text-white text-[7px] font-semibold px-2 py-1 rounded flex items-center gap-1">
                      <span>🗺</span> View in Google Maps
                    </button>
                    <div className="space-y-1 pt-1 border-t border-slate-100">
                      {[
                        { id: "1. Mainstain", time: "09:09 A..." },
                        { id: "2. Mainstain", time: "12:01 A..." },
                        { id: "3. Repair",    time: "03:01 A..." },
                      ].map((t, i) => (
                        <div key={i} className="flex items-center justify-between text-[7px]">
                          <span className="font-medium text-slate-700">{t.id}</span>
                          <span className="text-slate-400">{t.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right: Google-style Map */}
                  <div className="col-span-8 rounded-xl overflow-hidden border border-slate-200 relative" style={{ height: "130px" }}>
                    <div className="absolute inset-0 bg-[#E8F0F9]" />
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 300 130" fill="none">
                      {/* Road network */}
                      <line x1="0" y1="65" x2="300" y2="65" stroke="#FFFFFF" strokeWidth="4" />
                      <line x1="0" y1="90" x2="300" y2="90" stroke="#FFFFFF" strokeWidth="2.5" />
                      <line x1="0" y1="40" x2="300" y2="40" stroke="#FFFFFF" strokeWidth="2" />
                      <line x1="80" y1="0" x2="80" y2="130" stroke="#FFFFFF" strokeWidth="3" />
                      <line x1="160" y1="0" x2="160" y2="130" stroke="#FFFFFF" strokeWidth="3" />
                      <line x1="230" y1="0" x2="230" y2="130" stroke="#FFFFFF" strokeWidth="2" />
                      {/* Diagonal highway */}
                      <line x1="0" y1="20" x2="160" y2="90" stroke="#F0D080" strokeWidth="5" />
                      {/* Route line */}
                      <polyline points="80,40 130,65 160,65 200,90" stroke="#4A90D9" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                      {/* Green areas */}
                      <rect x="0" y="0" width="50" height="35" rx="2" fill="#C8E6C9" opacity="0.5" />
                      <rect x="170" y="0" width="50" height="35" rx="2" fill="#C8E6C9" opacity="0.5" />
                      <rect x="240" y="95" width="60" height="35" rx="2" fill="#C8E6C9" opacity="0.4" />
                      {/* Map Pins */}
                      <circle cx="80" cy="40" r="9" fill="#4A90D9" stroke="white" strokeWidth="1.5" />
                      <text x="80" y="44" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">1</text>
                      <circle cx="160" cy="65" r="9" fill="#4A90D9" stroke="white" strokeWidth="1.5" />
                      <text x="160" y="69" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">2</text>
                      <circle cx="200" cy="90" r="9" fill="#4A90D9" stroke="white" strokeWidth="1.5" />
                      <text x="200" y="94" textAnchor="middle" fill="white" fontSize="9" fontWeight="bold">3</text>
                      {/* Zoom buttons */}
                      <rect x="5" y="50" width="14" height="28" rx="3" fill="white" stroke="#D1D5DB" strokeWidth="0.5" />
                      <text x="12" y="62" textAnchor="middle" fill="#374151" fontSize="11" fontWeight="bold">+</text>
                      <text x="12" y="76" textAnchor="middle" fill="#374151" fontSize="11" fontWeight="bold">−</text>
                      {/* City labels */}
                      <text x="20" y="15" fill="#6B7280" fontSize="6" fontWeight="500">Schaerbeek</text>
                      <text x="165" y="20" fill="#6B7280" fontSize="6">Laeken</text>
                      <text x="240" y="115" fill="#6B7280" fontSize="6">Warre</text>
                      <text x="100" y="50" fill="#6B7280" fontSize="5">Bxl</text>
                    </svg>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. HVAC SERVICES: FULL DEVICE TRACEABILITY SECTION
   Inventory Lot/Serial Number detail card with MAINTAINED badge
   ───────────────────────────────────────────────────────────── */
export function HvacFullDeviceTraceabilitySection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="flex flex-col items-center gap-6">

          {/* HEADER: Centered title with orange strikethrough accent on "traceability" */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4 max-w-4xl"
          >
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight`}>
              Full device{" "}
              <span className="relative inline-block text-slate-900 dark:text-slate-100">
                traceability
                {/* Orange highlighter / strikethrough bar */}
                <span
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-[14px] rounded-sm pointer-events-none"
                  style={{ background: "rgba(251,146,60,0.55)", zIndex: -1 }}
                />
              </span>
            </h2>
            <p className="text-xl leading-relaxed text-gray-300">
              Access customer devices, serial numbers, and maintenance history instantly, enabling{" "}
              <strong className="text-white font-bold">faster troubleshooting</strong>,{" "}
              <strong className="text-white font-bold">warranty checks</strong>, and{" "}
              <strong className="text-white font-bold">proactive service management</strong>.
            </p>
          </motion.div>

          {/* ODOO INVENTORY DETAIL CARD */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="w-full flex justify-center relative"
          >
            {/* Thumbs-up doodle positioned bottom-right of card */}
            <div className="relative w-full max-w-3xl">

              {/* Odoo Inventory Window Card */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 text-[10px] sm:text-[11.5px] font-sans overflow-hidden">

                {/* Top Nav Bar */}
                <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
                  <div className="flex items-center gap-2.5">
                    {/* Inventory icon (orange gradient circle) */}
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 text-white text-[8px] font-bold flex-shrink-0">●</span>
                    <span className="font-semibold text-slate-700">Inventory</span>
                    <span className="text-slate-400">Overview</span>
                    <span className="text-slate-400">Operations</span>
                    <span className="text-slate-400">Products</span>
                    <span className="text-slate-400">Reporting</span>
                    <span className="text-slate-400">Configuration</span>
                  </div>
                  <div className="flex items-center gap-1 text-[7px] text-slate-400">
                    <span>🤖</span><span>💬</span><span>⏱</span><span>✕</span>
                    <span className="font-semibold text-slate-700 ml-1">BreezeMasters</span>
                    <span className="inline-flex w-4 h-4 rounded-full bg-emerald-400 items-center justify-center text-white text-[7px] font-bold">B</span>
                  </div>
                </div>

                {/* Breadcrumb + Action Bar */}
                <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] gap-1">
                  <div className="flex items-center gap-1.5">
                    <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                    <span className="text-slate-400">…</span>
                    <span className="text-[#7C5A76] font-medium">/ LG S12ET Spli…</span>
                    <span className="text-slate-400">/</span>
                    <span className="text-[#7C5A76] font-medium">Lots/Seria…</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[7px]">
                    <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 hover:bg-slate-50">
                      <span>📊</span><span>1 Sales</span>
                    </button>
                    <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 hover:bg-slate-50">
                      <span>💳</span><span>1 Purchases</span>
                    </button>
                    <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 hover:bg-slate-50">
                      <span>🚚</span><span>1 Transfers</span>
                    </button>
                    <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600 hover:bg-slate-50">
                      More ▾
                    </button>
                    <div className="ml-2 flex items-center gap-0.5 text-slate-400">
                      <span>📄</span>
                      <span>1 / 8</span>
                      <span className="border border-slate-200 rounded px-1">❮</span>
                      <span className="border border-slate-200 rounded px-1">❯</span>
                    </div>
                  </div>
                </div>

                {/* Record Row: S12ET-000001 + gear */}
                <div className="px-3 pt-2 pb-0.5 border-b border-slate-100 text-[7.5px] text-slate-500 flex items-center gap-1">
                  <span>S12ET-000001</span>
                  <span>⚙️</span>
                </div>

                {/* MAIN FORM BODY */}
                <div className="relative px-3 pt-3 pb-4 space-y-4">

                  {/* GREEN MAINTAINED RIBBON (top-right corner) */}
                  <div className="absolute top-0 right-0 overflow-hidden w-24 h-24 pointer-events-none">
                    <div
                      className="absolute top-4 right-[-22px] rotate-45 bg-emerald-600 text-white text-[7px] font-bold tracking-widest uppercase text-center"
                      style={{ width: "90px", padding: "4px 0" }}
                    >
                      MAINTAINED
                    </div>
                  </div>

                  {/* Lot/Serial Number label + value */}
                  <div className="space-y-0.5">
                    <div className="text-[7px] text-slate-400 font-medium flex items-center gap-0.5">
                      Lot/Serial Number <span className="text-slate-300">?</span>
                    </div>
                    <div className="text-xl font-bold text-slate-900 tracking-tight">S12ET-000001</div>
                  </div>

                  {/* Two column info grid */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-[7.5px]">

                    {/* Left: INFORMATION */}
                    <div className="space-y-2">
                      <div className="text-[7px] font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 pb-0.5">INFORMATION</div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                        <span className="text-slate-500 font-medium flex items-center gap-0.5">Product <span className="text-slate-300 text-[6px]">?</span></span>
                        <span className="text-slate-800 font-semibold">LG S12ET Split Airco 3.5kW</span>
                        <span className="text-slate-500 font-medium flex items-center gap-0.5">Reference <span className="text-slate-300 text-[6px]">?</span></span>
                        <span className="text-slate-300 italic text-[7px]">—</span>
                        <span className="text-slate-500 font-medium">Customer</span>
                        <span>
                          <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[7px] font-medium">Mike Brown</span>
                        </span>
                        <span className="text-slate-500 font-medium">Delivery</span>
                        <span className="text-amber-600 font-semibold">Today</span>
                      </div>
                    </div>

                    {/* Right: INVENTORY */}
                    <div className="space-y-2">
                      <div className="text-[7px] font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 pb-0.5">INVENTORY</div>
                      <div className="grid grid-cols-2 gap-x-2 gap-y-1.5">
                        <span className="text-slate-500 font-medium">On Hand Quantity</span>
                        <span className="text-slate-800 font-semibold">0.00 Units</span>
                        <span className="text-slate-500 font-medium">Location</span>
                        <span className="text-slate-800 font-semibold">Customers</span>
                      </div>
                    </div>

                  </div>

                  {/* DESCRIPTION section label */}
                  <div className="text-[7px] font-bold text-slate-500 uppercase tracking-wide border-b border-slate-100 pb-0.5 pt-1">DESCRIPTION</div>
                  <div className="h-4" /> {/* spacer for description area */}

                  {/* Bottom action bar */}
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <div className="flex items-center gap-1.5">
                      <button className="bg-[#7C5A76] text-white text-[7px] font-semibold px-2 py-1 rounded">Send message</button>
                      <button className="border border-slate-200 text-slate-600 text-[7px] px-2 py-1 rounded">Log note</button>
                      <button className="border border-slate-200 text-slate-600 text-[7px] px-2 py-1 rounded">Activity</button>
                    </div>
                    <div className="flex items-center gap-1 text-[7px] text-slate-400">
                      <span>🔍</span><span>🔗</span><span>👤</span>
                    </div>
                  </div>

                </div>
              </div>

              {/* Thumbs-up doodle emoji outside bottom-right of card */}
              <div
                className="absolute -bottom-4 -right-4 text-4xl select-none pointer-events-none"
                style={{ transform: "rotate(-8deg)" }}
                aria-hidden="true"
              >
                👍
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. HVAC SERVICES: RECURRING MAINTENANCE REVENUES SECTION
   Subscriptions sales order card (S00005) + title + wrench doodle
   ───────────────────────────────────────────────────────────── */
export function HvacRecurringMaintenanceRevenuesSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">

          {/* LEFT: Odoo Subscriptions / Sales Order card (max-w-[560px]) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 w-full flex justify-center lg:justify-start"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 font-sans overflow-hidden w-full max-w-5xl">

              {/* Top Nav Bar */}
              <div className="flex flex-wrap items-center justify-between bg-white border-b border-slate-100 px-3 py-1.5 text-[7.5px] text-slate-500 gap-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 text-white text-[8px] font-bold flex-shrink-0">●</span>
                  <span className="font-semibold text-slate-700">Subscriptions</span>
                  <span>Subscriptions</span>
                  <span>Products</span>
                  <span>Reporting</span>
                  <span>Configuration</span>
                </div>
                <div className="flex items-center gap-1 text-[7px] text-slate-400">
                  <span>🤖</span><span>💬</span><span>⏱</span><span>✕</span>
                  <span className="font-semibold text-slate-700 ml-1">BreezeMasters</span>
                  <span className="inline-flex w-4 h-4 rounded-full bg-emerald-400 items-center justify-center text-white text-[7px] font-bold">B</span>
                </div>
              </div>

              {/* Breadcrumb + Action buttons row */}
              <div className="flex flex-wrap items-center justify-between border-b border-slate-100 px-3 py-1.5 text-[7.5px] gap-1">
                <div className="flex items-center gap-1.5">
                  <span className="bg-[#7C5A76] text-white px-1.5 py-0.5 rounded font-semibold text-[7px]">New</span>
                  <span className="text-[#7C5A76] font-medium">Subscriptions</span>
                  <span className="text-slate-400">›</span>
                  <span className="text-slate-600 font-medium">S00000</span>
                  <span className="text-slate-400">⚙️</span>
                </div>
                <div className="flex items-center gap-1 text-[7px] text-slate-500">
                  <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50">
                    <span>📊</span><span>1 Projects</span><br /><span className="text-[6px] text-slate-400 ml-3">0 Tasks</span>
                  </button>
                  <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50">
                    <span>⏱</span><span>Recorded<br /><span className="text-[6px] text-slate-400">0 Hours</span></span>
                  </button>
                  <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50">
                    <span>🚚</span><span>Delivery<br /><span className="text-[6px] text-slate-400">1</span></span>
                  </button>
                  <button className="flex items-center gap-0.5 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-50">
                    <span>✓</span><span>MRR<br /><span className="text-[6px] text-slate-400">9.33 €</span></span>
                  </button>
                  <div className="ml-1 flex items-center gap-0.5 text-slate-400 text-[7px]">
                    <span>📄</span><span>1/2</span>
                    <span className="border border-slate-200 rounded px-0.5">❮</span>
                    <span className="border border-slate-200 rounded px-0.5">❯</span>
                  </div>
                </div>
              </div>

              {/* Status tabs row */}
              <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-1 text-[7.5px]">
                {["Create Invoice", "Send", "Preview", "Cancel", "Upsell", "Renew", "Close"].map((tab, i) => (
                  <button key={i} className={`px-1.5 py-0.5 rounded text-[7px] font-medium ${i === 0 ? "border border-slate-300 text-slate-700" : "text-slate-400"}`}>{tab}</button>
                ))}
                <div className="ml-auto flex items-center gap-1 text-[7px]">
                  <span className="text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Quotation</span>
                  <span className="text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">Quotation Sent</span>
                  <span className="bg-[#1A5E38] text-white rounded px-1.5 py-0.5 font-semibold">Sales Order</span>
                </div>
              </div>

              {/* Main form body */}
              <div className="px-3 pt-2.5 pb-3 space-y-3 text-[7.5px]">

                {/* SO number + In Progress badge */}
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold text-slate-900">S00005</div>
                  <span className="bg-emerald-100 text-emerald-700 text-[7px] font-semibold px-2 py-0.5 rounded-full">In Progress</span>
                </div>

                {/* Two-column: Customer address | Order details */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[7.5px]">
                  {/* Left */}
                  <div className="space-y-0.5">
                    <div className="text-slate-500">Customer</div>
                    <div className="text-[#7C5A76] font-semibold">Copper Spoon</div>
                    <div className="text-slate-600 text-[7px] leading-relaxed">
                      Rue Paul Reuter 6<br />
                      6700 Arlon<br />
                      Belgium
                    </div>
                    <div className="pt-1">
                      <span className="text-slate-500">Quotation Template</span>{" "}
                      <span className="text-[#7C5A76] font-semibold">Maintenance</span>
                    </div>
                  </div>
                  {/* Right */}
                  <div className="space-y-1">
                    {[
                      { label: "Order Date", value: "Sep 19, 2:37 PM" },
                      { label: "Recurring Plan", value: "Yearly", extra: "until" },
                      { label: "Next Invoice", value: "Oct 10", highlight: true },
                      { label: "Pricelist", value: "Default (EUR)", tip: true },
                      { label: "Payment Terms", value: "15 Days" },
                    ].map(({ label, value, extra, highlight, tip }, i) => (
                      <div key={i} className="grid grid-cols-2 gap-x-2">
                        <span className="text-slate-500 font-medium flex items-center gap-0.5">
                          {label}{tip && <span className="text-slate-300 text-[6px]">?</span>}
                        </span>
                        <span className={`font-semibold ${highlight ? "text-[#7C5A76]" : "text-slate-800"}`}>
                          {value}{extra && <span className="text-slate-400 font-normal ml-1">{extra}</span>}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Lines table */}
                <div className="border border-slate-200 rounded-lg overflow-hidden text-[7px]">
                  {/* Tab bar */}
                  <div className="flex items-center border-b border-slate-100 px-2 text-[7px]">
                    {["Order Lines", "Other Info", "Notes"].map((t, i) => (
                      <button key={i} className={`px-2 py-1 font-medium ${i === 0 ? "border-b-2 border-[#7C5A76] text-[#7C5A76]" : "text-slate-400"}`}>{t}</button>
                    ))}
                  </div>
                  {/* Table header */}
                  <div className="grid bg-slate-50 border-b border-slate-100 px-2 py-1 font-bold text-slate-500 gap-1" style={{ gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr 0.5fr 0.7fr 0.3fr" }}>
                    <span>Product</span><span>Serials</span><span>Quantity</span><span>Delivered</span><span>Invoiced</span><span>Unit</span><span>Unit Price</span><span>Taxes</span><span>Disc.%</span><span>Amount</span>
                  </div>
                  {/* Row 1: Maintenance */}
                  <div className="grid px-2 py-1 border-b border-slate-50 gap-1 items-start" style={{ gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr 0.5fr 0.7fr 0.3fr" }}>
                    <div>
                      <span className="text-[#7C5A76] font-semibold">Maintenance</span>
                      <div className="text-slate-400 text-[6px]">Price per year</div>
                      <div className="text-slate-400 text-[6px]">One appointment per year</div>
                    </div>
                    <span><span className="bg-amber-100 text-amber-800 px-1 rounded text-[6px] font-bold">B4R2A-5W001</span></span>
                    <span className="text-slate-700">1.00</span>
                    <span className="text-slate-700">6.00</span>
                    <span className="text-slate-700">0.00</span>
                    <span className="text-slate-700">Units</span>
                    <span className="text-slate-700">100.00</span>
                    <span className="text-slate-700">21%</span>
                    <span className="text-slate-700">9.00</span>
                    <span className="text-[#7C5A76] font-bold">100.00 €</span>
                  </div>
                  {/* Row 2: Refrigerant 134a */}
                  <div className="grid px-2 py-1 gap-1 items-center" style={{ gridTemplateColumns: "2fr 1fr 0.7fr 0.7fr 0.7fr 0.7fr 0.7fr 0.5fr 0.7fr 0.3fr" }}>
                    <span className="text-[#7C5A76] font-semibold">Refrigerant 134a</span>
                    <span></span>
                    <span className="text-slate-700">1.00</span>
                    <span className="text-slate-700">1.00</span>
                    <span className="text-slate-700">0.00</span>
                    <span className="text-slate-700">Units</span>
                    <span className="text-slate-700">1.00</span>
                    <span className="text-slate-700">21%</span>
                    <span className="text-slate-700">9.00</span>
                    <span className="text-[#7C5A76] font-bold">1.00 €</span>
                  </div>
                </div>

              </div>
            </div>
          </motion.div>

          {/* RIGHT: Title + wrench doodle + copywriting */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-5 text-left"
          >
            {/* Caveat handwritten title with red underline on "revenues" */}
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <span className="block">Recurring</span>
              <span className="block">maintenance</span>
              <span className="relative inline-block">
                revenues
                {/* Red/coral underline stroke */}
                <svg className="absolute -bottom-1 left-0 w-full h-4 overflow-visible" viewBox="0 0 180 10" fill="none">
                  <path d="M 2 6 Q 45 1, 90 6 T 178 6" stroke="#F87171" strokeWidth="5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h2>

            {/* Wrench circular doodle */}
            <div className="w-14 h-14">
              <svg viewBox="0 0 56 56" fill="none" className="w-full h-full">
                <circle cx="28" cy="28" r="25" stroke="#94A3B8" strokeWidth="2" strokeDasharray="5 3" />
                {/* Wrench icon */}
                <path d="M 18 36 L 32 22" stroke="#64748B" strokeWidth="3" strokeLinecap="round" />
                <circle cx="34" cy="20" r="5" stroke="#64748B" strokeWidth="2.5" fill="none" />
                <path d="M 30 24 L 17 37 L 19 39 L 32 26" fill="#64748B" />
                <circle cx="20" cy="37" r="3" fill="#64748B" />
                {/* Arrow arc (circular arrow) */}
                <path d="M 28 6 A 22 22 0 0 1 50 28" stroke="#0EA5E9" strokeWidth="2" strokeLinecap="round" fill="none" />
                <polygon points="50,22 50,28 44,28" fill="#0EA5E9" />
              </svg>
            </div>

            {/* Copywriting */}
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Transform one-off maintenance</strong> into ongoing service contracts with subscriptions and recurring tasks, keeping technicians busy, customers happy, and revenues predictable month after month.
            </p>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. HVAC SERVICES: REFRIGERANT REGULATIONS CHECK SECTION
   Left: title+copy | Right: Field Service form card + table card
   ───────────────────────────────────────────────────────────── */
export function HvacRefrigerantRegulationsSection({ section }) {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">

          {/* LEFT: Caveat title + copywriting */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4 space-y-5 text-left"
          >
            <h2 className={`${caveat.className} text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.05]`}>
              <span className="block">Refrigerant</span>
              <span className="block">regulations,</span>
              {/* "check!" with teal oval stroke around it */}
              <span className="relative inline-block">
                <span className="relative z-10">check!</span>
                <svg
                  className="absolute -inset-x-2 -inset-y-1 w-[calc(100%+16px)] h-[calc(100%+8px)] overflow-visible pointer-events-none"
                  viewBox="0 0 120 44"
                  fill="none"
                >
                  <ellipse cx="60" cy="22" rx="56" ry="19" stroke="#2DD4BF" strokeWidth="3.5" strokeLinecap="round" fill="none" />
                </svg>
              </span>
            </h2>
            <p className="text-xl leading-relaxed text-gray-300">
              <strong className="text-white font-bold">Monitor refrigerant manipulations</strong> during interventions and adhere to monitoring regulations.
            </p>
          </motion.div>

          {/* RIGHT: Two overlapping cards + "Done!" doodle */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-8 relative w-full flex justify-center lg:justify-end"
          >
            <div className="relative w-full max-w-5xl">

              {/* "Done!" label + arrow doodle (top-right) */}
              <div className="absolute -top-8 right-4 flex items-center gap-1 pointer-events-none select-none" style={{ zIndex: 20 }}>
                <svg className="w-6 h-8" viewBox="0 0 28 36" fill="none">
                  {/* curved arrow pointing down-left */}
                  <path d="M 24 4 Q 26 20, 8 32" stroke="#7C5A76" strokeWidth="2" strokeLinecap="round" fill="none" />
                  <polygon points="4,30 10,34 12,26" fill="#7C5A76" />
                </svg>
                <span className={`${caveat.className} text-xl font-bold text-slate-800 dark:text-white`}>
                  <span className="text-teal-400">🟢</span>Done!
                </span>
              </div>

              {/* BACKGROUND CARD: Refrigerant tracking table (bottom-right, offset) */}
              <div
                className="absolute right-0 bottom-0 w-[260px] bg-white rounded-xl shadow-xl border border-slate-200 text-slate-800 text-[7.5px] font-sans overflow-hidden"
                style={{ zIndex: 5, transform: "translate(12px, 18px)" }}
              >
                {/* Table */}
                <table className="w-full text-[7px]">
                  <thead className="bg-slate-50 border-b border-slate-100">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold text-slate-600">⊟ Total</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Added Refrigerant</th>
                      <th className="text-right px-2 py-1.5 font-semibold text-slate-600">Removed Refrigerant</th>
                    </tr>
                  </thead>
                  <tbody className="text-[7px]">
                    {/* Refrigerant 134a expandable row */}
                    <tr className="border-b border-slate-50 bg-slate-50/50">
                      <td colSpan={3} className="px-2 py-1 font-semibold text-slate-700">
                        <span className="text-slate-400 mr-1">⊕</span> Refrigerant 134a
                      </td>
                    </tr>
                    <tr className="border-b border-slate-50 font-bold bg-white">
                      <td className="px-2 py-1 text-slate-700">⊟ Total</td>
                      <td className="px-2 py-1 text-right text-slate-900">14.40</td>
                      <td className="px-2 py-1 text-right text-slate-900">1.20</td>
                    </tr>
                    <tr className="border-b border-slate-50">
                      <td className="px-2 py-1 pl-4 text-slate-600"><span className="text-slate-400 mr-1">⊕</span> January 2025</td>
                      <td className="px-2 py-1 text-right text-slate-700">2.20</td>
                      <td className="px-2 py-1 text-right text-slate-300">—</td>
                    </tr>
                    <tr>
                      <td className="px-2 py-1 pl-4 text-slate-600"><span className="text-slate-400 mr-1">⊕</span> September 2025</td>
                      <td className="px-2 py-1 text-right text-slate-700">12.20</td>
                      <td className="px-2 py-1 text-right text-slate-700">1.20</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* FOREGROUND CARD: Field Service mobile form */}
              <div
                className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 text-slate-800 text-[8px] font-sans overflow-hidden w-full max-w-[300px]"
                style={{ zIndex: 10 }}
              >
                {/* Mobile top bar */}
                <div className="flex items-center justify-between bg-white border-b border-slate-100 px-3 py-2 text-[7.5px] text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-500">☰</span>
                    <span className="font-semibold text-slate-700 text-[8px]">← Installation</span>
                  </div>
                  <div className="flex items-center gap-1 text-[7px] text-slate-400">
                    <span>🤖</span><span>💬</span><span>⏱</span>
                    <span className="inline-flex w-4 h-4 rounded-full bg-emerald-400 items-center justify-center text-white text-[7px] font-bold">B</span>
                  </div>
                </div>

                {/* Icon buttons row */}
                <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-1.5 text-[7.5px]">
                  <button className="border border-slate-200 rounded p-1 text-slate-500">⚙️</button>
                  <div className="ml-auto">
                    <button className="border border-slate-200 rounded p-1 text-slate-500">🗑️</button>
                  </div>
                </div>

                {/* Form fields */}
                <div className="px-3 py-3 space-y-3 text-[7.5px]">

                  {/* Operation tabs */}
                  <div className="space-y-1">
                    <div className="font-bold text-slate-500 uppercase text-[6.5px] tracking-wide">Operation</div>
                    <div className="flex items-center gap-1">
                      <button className="bg-[#1A73E8] text-white px-2 py-0.5 rounded text-[7px] font-semibold">Installation</button>
                      <button className="border border-slate-200 text-slate-500 px-2 py-0.5 rounded text-[7px]">Maintenance</button>
                      <button className="border border-slate-200 text-slate-500 px-2 py-0.5 rounded text-[7px]">Repair</button>
                    </div>
                  </div>

                  {/* Date */}
                  <div className="space-y-0.5 border-b border-slate-100 pb-2">
                    <div className="font-bold text-slate-500 uppercase text-[6.5px] tracking-wide">Date</div>
                    <div className="text-slate-800 font-semibold">Sep 10</div>
                    <div className="h-px w-full bg-slate-200 mt-1" />
                  </div>

                  {/* REFRIGERANT section */}
                  <div className="space-y-1.5">
                    <div className="font-bold text-slate-400 uppercase text-[6.5px] tracking-wide">REFRIGERANT</div>

                    <div className="space-y-0.5">
                      <div className="text-slate-500 text-[7px]">Refrigerant Product</div>
                      <div className="text-slate-800 font-semibold">Refrigerant 134a</div>
                      <div className="h-px w-full bg-slate-200" />
                    </div>

                    <div className="space-y-0.5">
                      <div className="text-slate-500 text-[7px]">Added Refrigerant</div>
                      <div className="text-slate-800 font-semibold">1.20</div>
                      <div className="h-px w-full bg-slate-200" />
                    </div>
                  </div>

                  {/* CONFORMITY section */}
                  <div className="space-y-1">
                    <div className="font-bold text-slate-400 uppercase text-[6.5px] tracking-wide">CONFORMITY</div>
                    <div className="flex items-center gap-1 text-[7.5px]">
                      <span className="inline-flex items-center justify-center w-3 h-3 rounded border border-[#1A73E8] bg-[#1A73E8] text-white text-[6px] font-bold">✓</span>
                      <span className="text-slate-600">Product is functional at...</span>
                    </div>
                    <div className="space-y-0.5">
                      <div className="text-slate-500 text-[7px]">Comments</div>
                      <div className="text-slate-700 italic">All went fine!</div>
                    </div>
                  </div>

                  {/* Worker Signature */}
                  <div className="space-y-1 border-t border-slate-100 pt-2">
                    <div className="text-slate-500 text-[7px]">Worker Signature</div>
                    <svg viewBox="0 0 80 28" className="w-20 h-7" fill="none">
                      {/* Cursive "Dth" style signature */}
                      <path
                        d="M 8 20 Q 12 6, 18 18 Q 22 28, 26 16 Q 30 6, 34 14 Q 38 20, 44 12 Q 50 4, 54 16 Q 58 26, 64 18 Q 68 12, 72 20"
                        stroke="#3B4ECC"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  </div>

                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
